import { generatePaymentPlans, roundToNearestHundred } from "@/lib/utils";

export default {
  data() {
    return ({
      item: {},
      selected: {},
      quantity: 1,
      views: { more: false, quantity: false, summary: false, reviews: false, payment: false },
      liked: false,
      variations: {
        size: null,
      },
      toast: {
        visible: false,
        color: 'orange',
        message: ''
      },
      buy_on_credit: false
    });
  },

  metaInfo() {
    return {
      title: this.item ? this.item.name : 'Item',
    }
  },

  computed: {
    sizes() {
      return (this.item.sizes && this.item.sizes !== 'null') ? this.item.sizes.split(',') : [];
    },
    itemRating() {
      return Math.round(this.item.ratings / this.item.total_ratings)
    },
    discount() {
      if (!this.item || !this.item.id) return null;
      const { price, discount_price, discount_expiry_date } = this.item
      const discount = {};
      if (discount_price) {
        const diff = price - discount_price;
        const percentage = Math.round((diff * 100) / price);
        if (discount_expiry_date) {
          const end = new Date(discount_expiry_date);
          if (new Date(Date.now()) < end) {
            discount.price = discount_price
            discount.end = end
            discount.percentage = percentage
          }
        } else {
          discount.price = discount_price
          discount.percentage = percentage
        }
      }
      return discount.price ? discount : null;
    },
    price() {
      const { discount_price, credit_price, price } = this.item;
      if (this.discount) return discount_price
      else return this.buy_on_credit ? credit_price || price : price
    },
    credit() {
      return this.item && this.item.sell_on_credit === 1
    },
    plans() {
      let plans = [];
      if (!this.item.id) return plans;
      const { no_of_months, upfront_amount } = this.item;
      const price = this.price;
      return generatePaymentPlans({
        price,
        no_of_months,
        upfront_amount,
        credit: this.credit,
        quantity: this.quantity
      });
    },
  },

  methods: {
    incrementQuantity() {
      if (this.item.quantity === 1) this.showToast('Only one item available', 'orange');
      this.quantity = Math.min((this.item.maximum_order || 10), this.quantity + 1, this.item.quantity || 1)
    },

    decrementQuantity() {
      if (this.item.quantity === 1) this.showToast('Only one item available', 'orange');
      this.quantity = Math.max(1, this.quantity - 1)
    },

    handlePlanChange(plan, add = false) {
      this.selected = plan;
      if (add) this.addToCart()
    },

    closeReview(l) {
      this.item.total_ratings = l;
      this.views.reviews = false
    },

    async addView() {
      const views = JSON.parse(localStorage.getItem('views')) || [];
      if (!views.includes(this.item.id)) {
        await this.$http.get(`/item/${ this.item.id }/view`);
        views.push(this.item.id);
        localStorage.setItem('views', JSON.stringify(views));
      }
    },

    async addLike() {
      if (!this.liked) {
        const res = await this.$http.get(`/item/${ this.item.id }/like`);
        if (res.data.status === true) {
          const likes = JSON.parse(localStorage.getItem('likes')) || []
          likes.push(this.item.id);
          localStorage.setItem('likes', JSON.stringify(likes))
          this.liked = true;
          this.item.likes++
        }
      }
    },

    variationsSelected() {
      let valid = true;
      if (this.sizes.length && !this.variations.size) {
        this.showToast('Please select a size');
        valid = false;
      }
      return valid;
    },

    showToast(message, color = '') {
      this.toast.visible = true;
      this.toast.message = message;
      if (color) this.toast.color = color
      setTimeout(() => {
        this.toast = { visible: false, message: '', color: '' }
      }, 5000)
    },

    addToCart() {
      if (!this.variationsSelected()) return;
      const plan = this.credit
        ? { ...this.selected, id: this.item.id, quantity: this.quantity, variations: this.variations }
        : {
          id: this.item.id,
          quantity: this.quantity,
          monthly_amount: 0,
          no_of_months: 0,
          upfront_amount: this.price,
          sell_on_credit: 0,
          variations: this.variations
        };

      this.$store.commit('cart/addItem', { ...this.item, plan });
      this.showView();
      this.$emit('close');
      this.$bus.$emit('cart');
    },

    showView(view) {
      Object.keys(this.views).forEach(key => {
        this.views[key] = false
      });
      if (view) this.views[view] = true
    },

    async handleStateChange(pid) {
      const { items, merchantItems, searchedItems, filteredItems } = this.$store.state.catalogue;
      this.item = [...merchantItems, ...items, ...searchedItems, ...filteredItems].find(item => item.slug === pid);
      if (!this.item) await this.getItem()
      this.checkIfPartnershipEnabled()
      this.buy_on_credit = this.item.sell_on_credit === 1;
      this.selectPlan(this.buy_on_credit);
      this.showView();
      this.addView();
      this.liked = (JSON.parse(localStorage.getItem('likes')) || []).includes(this.item.id);
    },

    checkIfPartnershipEnabled() {
      if (this.$store.state.merchant.partnership === 1) {
        this.item.sell_on_credit = 1
        const { offer = {} } = this.item;
        this.item.no_of_months = offer.duration || this.item.no_of_months || 3
        this.item.credit_price = +offer.credit_amount || +this.item.price
      } else {
        if (!this.item.no_of_months) this.item.sell_on_credit = 0
      }
    },

    async getItem() {
      const { pid } = this.$route.params;
      const id = await this.$store.dispatch('catalogue/resolveItemId', pid);
      // eslint-disable-next-line no-unused-vars
      const { data: { data: { merchant, ...item } } } = await this.$http.get(`/item/${ id || pid }`);
      this.item = item;
    },

    selectPlan(credit) {
      if (credit) {
        this.selected = {
          monthly_amount_estimate: roundToNearestHundred((+this.price) / (this.item.no_of_months)),
          monthly_amount: roundToNearestHundred((+this.item.price) / (this.item.no_of_months)),
          no_of_months: this.item.no_of_months,
          upfront_amount: this.item.upfront_amount || 0,
          sell_on_credit: 1
        }
      } else {
        this.selected = {
          monthly_amount: 0,
          no_of_months: 0,
          upfront_amount: this.price,
          sell_on_credit: 0
        }
      }
    },

    applyWatchers() {
      this.$watch('$route', () => {
        const { pid } = this.$route.params;
        if (this.item) {
          if (this.item.id && this.item.id !== +pid) this.handleStateChange(pid);
        }
      })
    }
  },

  watch: {
    buy_on_credit(value) {
      this.selectPlan(value)
    }
  }
}
